/**
 * Team @mentions (TEAM_MENTIONS_FEATURE) in PLAIN text: task comments.
 *
 * A plain-text mention is just "@Full Name" in the text. The writer's picks are kept beside the
 * text (id + name), and on submit only the picks whose "@Full Name" is still in the text are sent
 * as `mentioned_user_ids` — deleting the name un-mentions the person.
 */

/** `kind` is "team" for a team picked with TEAMS_FEATURE; absent or "user" is a person. */
export type MentionPick = { id: number; name: string; kind?: "user" | "team" };

const isTeamPick = (pick: MentionPick) => pick.kind === "team";

/** The backend caps a comment at 20 mentions; so does the client. */
export const MAX_MENTIONS = 20;

const WHITESPACE = /\s/;

/**
 * The "@kar" being typed at `caret`, if any: an "@" at the start of the text or after whitespace,
 * followed by non-space characters up to the caret. Like the editor picker, an email address
 * ("jo@clinic.com") never opens it, and the search stops at a space.
 */
export const getActiveMentionQuery = (
  text: string,
  caret: number | null | undefined
): { start: number; query: string } | null => {
  if (caret == null || caret < 1) return null;
  const before = text.slice(0, caret);
  const at = before.lastIndexOf("@");
  if (at < 0) return null;
  if (at > 0 && !WHITESPACE.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (WHITESPACE.test(query)) return null;
  return { start: at, query };
};

/** Replaces the "@kar" between `start` and `caret` with "@Karen Ruiz " and returns the new caret. */
export const insertMention = (
  text: string,
  start: number,
  caret: number,
  name: string
): { text: string; caret: number } => {
  const inserted = `@${name} `;
  const after = text.slice(caret).replace(/^ /, "");
  return {
    text: `${text.slice(0, start)}${inserted}${after}`,
    caret: start + inserted.length
  };
};

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Ids of the picks whose "@Full Name" is still in the text, at most 20.
 *
 * Counted per occurrence, longest name first: "@Ann Lee" is never also read as "@Ann", and when
 * the same name was picked twice (the writer deleted one Chris Lee and picked the other) only as
 * many picks as there are "@Chris Lee" occurrences count, latest pick first.
 */
export const getMentionedIdsInText = (
  text: string,
  picks: MentionPick[]
): number[] => getMentionedPickIds(text, picks, false);

/** Ids of the picked TEAMS whose "@Team name" is still in the text (TEAMS_FEATURE). */
export const getMentionedTeamIdsInText = (
  text: string,
  picks: MentionPick[]
): number[] => getMentionedPickIds(text, picks, true);

const getMentionedPickIds = (
  text: string,
  allPicks: MentionPick[],
  teams: boolean
): number[] => {
  const picks = allPicks.filter((pick) => isTeamPick(pick) === teams);
  const counts = new Map<string, number>();
  splitTextByMentions(
    text,
    picks.map((pick) => pick.name)
  ).forEach((segment) => {
    if (!segment.isMention) return;
    const name = segment.text.slice(1);
    counts.set(name, (counts.get(name) ?? 0) + 1);
  });
  const keptIds = new Set<number>();
  [...picks].reverse().forEach((pick) => {
    if (keptIds.has(pick.id)) return;
    const remaining = counts.get(pick.name) ?? 0;
    if (remaining > 0) {
      keptIds.add(pick.id);
      counts.set(pick.name, remaining - 1);
    }
  });
  // In the order people were first picked.
  const ids: number[] = [];
  const added = new Set<number>();
  picks.forEach((pick) => {
    if (
      keptIds.has(pick.id) &&
      !added.has(pick.id) &&
      ids.length < MAX_MENTIONS
    ) {
      added.add(pick.id);
      ids.push(pick.id);
    }
  });
  return ids;
};

/** Whether a teammate's name matches what is typed after "@" (any word starts with it). */
export const candidateMatchesQuery = (name: string, query: string): boolean => {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const lower = name.toLowerCase();
  return (
    lower.startsWith(needle) ||
    lower.split(/\s+/).some((word) => word.startsWith(needle))
  );
};

/** `start` is the run's offset in the text: unique and stable, so it is the React key. */
export type MentionTextSegment = {
  text: string;
  isMention: boolean;
  start: number;
};

/**
 * Splits a comment into plain runs and "@Full Name" runs for the people it mentions, so the names
 * can be highlighted. Longest names win ("@Ann Lee" before "@Ann").
 */
export const splitTextByMentions = (
  text: string,
  names: string[]
): MentionTextSegment[] => {
  const unique = Array.from(new Set(names.filter(Boolean))).sort(
    (a, b) => b.length - a.length
  );
  if (!text || !unique.length)
    return text ? [{ text, isMention: false, start: 0 }] : [];
  const pattern = new RegExp(
    `@(?:${unique.map(escapeRegExp).join("|")})(?![\\p{L}\\p{N}])`,
    "gu"
  );
  const segments: MentionTextSegment[] = [];
  let last = 0;
  let match = pattern.exec(text);
  while (match) {
    if (match.index > last) {
      segments.push({
        text: text.slice(last, match.index),
        isMention: false,
        start: last
      });
    }
    segments.push({ text: match[0], isMention: true, start: match.index });
    last = match.index + match[0].length;
    match = pattern.exec(text);
  }
  if (last < text.length) {
    segments.push({ text: text.slice(last), isMention: false, start: last });
  }
  return segments;
};
